import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { CashRegisterService } from '../../../../services/cash-register/cash-register.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { CashRegister } from '../../../../interfaces/cash-register';
import { brandNameOrDefault, formatBRL, formatDateTimeBR, openPrintWindow, receiptLogoHtml } from '../../../../utils/print-receipt.util';
import { BrandService } from '../../../../services/brand/brand.service';

@Component({
  selector: 'app-sangria',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink],
  templateUrl: './sangria.component.html',
})
export class SangriaComponent implements OnInit {
  caixas: CashRegister[] = [];
  caixasDestino: CashRegister[] = [];
  caixaOrigem: CashRegister | null = null;
  caixaDestinoId: number | null = null;
  isLoadingDestinos = false;
  valor: number | null = null;
  observacao: string = '';
  historico: string = '';
  dataTransacao: string = '';
  erro: string | null = null;
  isSubmitting = false;
  sucesso: { nrSequencia: number; valor: number; destino: string } | null = null;
  private username = '';
  private clinicId = 0;

  constructor(
    private router: Router,
    private cashRegisterService: CashRegisterService,
    private paymentService: PaymentService,
    private localStorage: LocalStorageService,
    private brandService: BrandService,
  ) {}

  async ngOnInit() {
    this.username = this.localStorage.get('login') as string;
    this.clinicId = Number(this.localStorage.get('currentUnit'));

    if (!this.username || !this.clinicId) {
      this.router.navigate(['/home']);
      return;
    }

    this.caixas = await this.cashRegisterService.getByUnit(this.clinicId, this.username);
    this.caixaOrigem = this.caixas[0] ?? null;
    this.dataTransacao = this.nowLocalISO();
    await this.carregarDestinos();
  }

  async onOrigemChange() {
    this.caixaDestinoId = null;
    await this.carregarDestinos();
  }

  private async carregarDestinos() {
    if (!this.caixaOrigem) {
      this.caixasDestino = [];
      return;
    }
    this.isLoadingDestinos = true;
    try {
      this.caixasDestino = await this.cashRegisterService.getForBleed(
        this.caixaOrigem.establishmentId,
        this.caixaOrigem.sequence,
        this.username,
      );
    } finally {
      this.isLoadingDestinos = false;
    }
  }

  onObservacaoChange() {
    this.historico = this.observacao;
  }

  get saldoOrigem(): number {
    return this.caixaOrigem?.currentBalance ?? 0;
  }

  valorExcedeSaldo(): boolean {
    return !!this.valor && !!this.caixaOrigem && this.valor > this.saldoOrigem;
  }

  formValido(): boolean {
    return (
      !!this.caixaOrigem &&
      !!this.caixaDestinoId &&
      !!this.valor &&
      this.valor > 0 &&
      !this.valorExcedeSaldo() &&
      this.observacao.trim().length > 0
    );
  }

  async transferir() {
    if (!this.formValido() || this.isSubmitting) return;

    this.isSubmitting = true;
    this.erro = null;
    try {
      const destino = this.caixas.find(c => c.sequence === this.caixaDestinoId);
      const dtTransacao = this.dataTransacao
        ? new Date(this.dataTransacao).toISOString()
        : new Date().toISOString();

      const result = await this.paymentService.postCashBleed({
        nrSeqCaixaOrigem: this.caixaOrigem!.sequence,
        nrSeqCaixaDestino: this.caixaDestinoId!,
        vlSangria: this.valor!,
        dtTransacao,
        dsObservacao: this.observacao,
        dsHistorico: this.historico,
        nmUsuario: this.username,
        cdEstabelecimento: this.caixaOrigem!.establishmentId,
      });

      this.sucesso = {
        nrSequencia: result.nrSequencia,
        valor: this.valor!,
        destino: destino?.name || '',
      };
    } catch (e: any) {
      this.erro = e?.error?.feedback || e?.message || 'Erro ao efetivar sangria.';
    } finally {
      this.isSubmitting = false;
    }
  }

  imprimirRecibo() {
    if (!this.sucesso || !this.caixaOrigem) return;
    const destino = this.caixas.find(c => c.sequence === this.caixaDestinoId)?.name || this.sucesso.destino;
    const html = `
      <div class="header">
        <div class="logo">${receiptLogoHtml(this.brandService.logo, this.brandService.cdEmpresa)}</div>
        <div style="text-align: right;">
          <p class="title">Comprovante de Sangria</p>
          <p class="subtitle">Transferência entre caixas</p>
          <p class="receipt-number">Nº ${this.sucesso.nrSequencia}</p>
        </div>
      </div>
      <table class="data">
        <tr><td class="label">Caixa Origem</td><td class="value">${this.caixaOrigem.name} — ${this.caixaOrigem.establishmentName}</td></tr>
        <tr><td class="label">Caixa Destino</td><td class="value">${destino}</td></tr>
        <tr><td class="label">Data/Hora</td><td class="value">${formatDateTimeBR(this.dataTransacao || new Date())}</td></tr>
        <tr><td class="label">Usuário</td><td class="value">${this.username}</td></tr>
      </table>
      <div class="valor-box">
        <div style="font-size: 10px; color: #626272;">Valor transferido</div>
        <div class="amount">${formatBRL(this.sucesso.valor)}</div>
      </div>
      <div class="obs-box">
        <div class="label">Observação</div>
        <div>${this.observacao || '—'}</div>
      </div>
      <div class="obs-box">
        <div class="label">Histórico</div>
        <div>${this.historico || '—'}</div>
      </div>
      <div class="signature">
        <div class="line"></div>
        <div class="name">Assinatura do responsável</div>
      </div>
      <div class="footer">${brandNameOrDefault(this.brandService.brandName)} — Comprovante sem valor fiscal</div>
    `;
    openPrintWindow(`Sangria ${this.sucesso.nrSequencia}`, html);
  }

  novaSangria() {
    this.sucesso = null;
    this.valor = null;
    this.caixaDestinoId = null;
    this.observacao = '';
    this.historico = '';
    this.dataTransacao = this.nowLocalISO();
    this.erro = null;
  }

  voltar() {
    this.router.navigate(['/lancamentos-financeiros']);
  }

  formatCurrency(value: number): string {
    return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'R$ 0,00';
  }

  private nowLocalISO(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
